import { forEach } from 'lodash';
import { makeObservable } from 'shared/stores/core/make-observable';
import { functions } from 'shared/util';

/**
 * Provides a static install method on the class so it can be injected into a DI container
 * @param name
 */
export function injectable (name) {
  return function (target) {
    target.install = function ({factory}) {
      factory.register(name, target);
    };
  };
}

/**
 * Provides a static install method on the class so it can be injected in the Store
 * @param Target
 */
export function installable (Target) {
  Target.install = function (di) {
    return new Target(di);
  };
}

/**
 * Automatically exposes all function names on a class as `dispatch`able actions, binding "this" as well. If a "remap"
 * object is provided on the prototype it'll forward the original action to the requested action.
 * @param target
 */
export function actionProvider (target) {
  return class ActionProviderProxy extends target {
    constructor ({dispatcher}) {
      super(...arguments);
      const self = this;

      functions(target.prototype).forEach(key => {
        dispatcher.register(key, self[key].bind(self));
      });

      if (self.remap) {
        const remap = self.remap;
        forEach(remap, (action, key) => {
          dispatcher.register(key, ({dispatch}, ...args) => dispatch(action, ...args));
        });
      }
    }
  };
}

/**
 * Automatically imports a class's "state" property into the stateMap
 * @param name
 * @returns {Function}
 */
export function stateProvider (name) {
  return function (target) {
    return class StateProviderProxy extends target {
      constructor ({stateMap}) {
        super(...arguments);
        const self = this;
        let state = (typeof self.state === 'function') ? self.state() : self.state;
        if (state) {
          state = makeObservable(state);
          self.state = state;
          stateMap.set(name, state);
        }
      }
    };
  };
}

/**
 * Automatically imports a class's "getter" property into the getters map. Unlike Vuex the results of these getters
 * are not cached are always get re-evaluated.
 * @param name
 * @returns {Function}
 */
export function getterProvider (name) {
  return function (target) {
    return class GetterProviderProxy extends target {
      constructor (store) {
        super(...arguments);
        const self = this;
        const localGetters = {};
        if (self.getters) {
          forEach(self.getters, (fn, key) => {
            Object.defineProperty(localGetters, key, {
              get: () => fn.call(self, store)
            });
          });
          self.getters = localGetters;
          store.getters.set(name, self.getters);
        }
      }
    };
  };
}

/**
 * Applies the installable, stateProvider and actionProvider decorators to a class
 * @param name
 * @returns {Function}
 */
export function store (name) {
  return function (target) {
    let proxy = actionProvider(target);
    proxy = stateProvider(name)(proxy);
    proxy = getterProvider(name)(proxy);
    installable(proxy);
    return proxy;
  };
}



// WEBPACK FOOTER //
// ./src/shared/stores/core/decorators.js